import {
  createRouter,
  createWebHistory,
  isNavigationFailure,
  NavigationFailureType,
} from 'vue-router';
import { useAuthStore } from '@/core/stores/auth.store';
import { PATIENT_AUTH_ENTRY_PATH } from '@/core/config/authentication';
import routes from './routes';
import { APP_ONBOARDING_PATH, APP_ONBOARDING_STATUS } from '@/shared/constants/authentication';

const CHANGE_PASSWORD_PATH = '/auth/change-password';
const HOME_PATH = '/home';
const PROFILE_PATH = '/profile';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

// Navigation guard to check authentication
router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  const isAuthScreen = Boolean(to.meta.authScreen);

  // Routes that neither require a session nor belong to the sign-in flow
  if (!to.meta.requiresAuth && !isAuthScreen) {
    return true;
  }

  if (!authStore.isAuthReady) {
    await authStore.initAuth();
  }

  if (!authStore.isLoggedIn) {
    // An unexpired challenge opens only the replacement form, not protected App pages.
    const challenge = authStore.pendingPasswordChange;
    if (challenge && challenge.expiresAt > Date.now()) {
      return redirectToPath(to.path, CHANGE_PASSWORD_PATH);
    }

    authStore.pendingPasswordChange = null;
    return redirectToPath(to.path, PATIENT_AUTH_ENTRY_PATH);
  }

  if (isAuthScreen) {
    return redirectToPath(to.path, signedInStartPath(authStore));
  }

  // Server-owned onboarding state applies equally to links, reloads and Back navigation.
  if (authStore.appOnboardingStatus === APP_ONBOARDING_STATUS.PENDING_TERMS_ACCEPTANCE) {
    if (to.path === APP_ONBOARDING_PATH.TERMS) {
      return true;
    }
    return { path: APP_ONBOARDING_PATH.TERMS };
  }
  if (authStore.appOnboardingStatus === APP_ONBOARDING_STATUS.IN_PROGRESS) {
    if (to.path === APP_ONBOARDING_PATH.SETUP) {
      return true;
    }
    return { path: APP_ONBOARDING_PATH.SETUP };
  }
  if (to.meta.onboarding) {
    return redirectToPath(to.path, signedInStartPath(authStore));
  }

  return true;
});

router.onError(() => console.error('[router.onError] Navigation failed'));
router.afterEach((_to, _from, failure) => {
  if (failure && isNavigationFailure(failure)) {
    if (isNavigationFailure(failure, NavigationFailureType.duplicated)) return;
    // An active form can intentionally keep its route while saving.
    if (isNavigationFailure(failure, NavigationFailureType.aborted)) {
      return;
    }
    console.error('[router.afterEach failure]', {
      type: failure.type,
    });
  }
});

// A caregiver starts on Profile; every other signed-in user starts on Home.
function signedInStartPath(authStore) {
  if (authStore.isActingAsCaregiver) {
    return PROFILE_PATH;
  }

  return HOME_PATH;
}

// Route guards already have the two paths; no route object conversion is needed.
const redirectToPath = (currentPath, targetPath) => {
  return currentPath === targetPath ? true : { path: targetPath };
};

export default router;

export async function redirectToOnboarding(status, requestToken) {
  const auth = useAuthStore();
  if (!auth.requireOnboarding(status, requestToken)) {
    return;
  }
  // Reuse the navigation guard's destination.
  await router.replace({ path: signedInStartPath(auth) });
}
